import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Target,
  Clock,
  AlertCircle,
  TrendingUp,
  Calendar,
  User,
  FolderOpen
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import milestoneService from '../../../services/milestoneService'
import RevisionHistoryModal from '../../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../../components/ConfirmationModal/ConfirmationModal.css'

const MilestoneList = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalMilestones: 0,
    notStarted: 0,
    inProgress: 0,
    completed: 0,
    onHold: 0,
    cancelled: 0,
    delayed: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedMilestoneData, setSelectedMilestoneData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [milestonesResponse, statsResponse] = await Promise.all([
        milestoneService.getAllMilestones(),
        milestoneService.getMilestoneStats()
      ])

      if (milestonesResponse.success !== false && milestonesResponse.milestones) {
        setData(milestonesResponse.milestones || [])
        
        // Show toast if no data
        if ((milestonesResponse.milestones || []).length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No milestones found. Create your first milestone to get started!')
        }
      } else {
        toast.error(milestonesResponse.message || 'Failed to load milestones')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data || {})
      }
    } catch (error) {
      console.error('Error loading data:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load milestones data')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    navigate('/projects/milestones/new')
  }

  const handleEdit = (milestone) => {
    const milestoneId = milestone._id || milestone.id
    if (!milestoneId) {
      toast.error('Milestone ID not found')
      return
    }
    navigate(`/projects/milestones/edit/${milestoneId}`)
  }

  const handleView = (milestone) => {
    const milestoneId = milestone._id || milestone.id
    if (!milestoneId) {
      toast.error('Milestone ID not found')
      return
    }
    navigate(`/projects/milestones/view/${milestoneId}`)
  }

  const handleDelete = (milestone) => {
    setDeleteItem(milestone)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const milestoneId = deleteItem._id || deleteItem.id
      if (!milestoneId) {
        toast.error('Milestone ID not found')
        return
      }
      const response = await milestoneService.deleteMilestone(milestoneId)
      if (response.success) {
        toast.success('Milestone deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete milestone')
      }
    } catch (error) {
      console.error('Error deleting milestone:', error)
      if (!error._errorHandled) {
        toast.error('Failed to delete milestone')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (milestone) => {
    if (!milestone) {
      toast.error('Milestone data not found')
      return
    }
    setSelectedMilestoneData(milestone)
    setShowRevisionHistory(true)
  }

  const handleRowSelection = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  const handleSelectionChange = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const day = String(date.getDate()).padStart(2, '0')
    const month = months[date.getMonth()]
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  // Define columns for the data table
  const columns = [
    {
      key: 'milestoneNumber',
      label: 'Milestone #',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'milestoneName',
      label: 'Milestone Name',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'project',
      label: 'Project',
      sortable: true,
      render: (value, row) => {
        const project = row.project
        if (!project) return <span className="text-gray-500">N/A</span>
        const projectName = typeof project === 'string' 
          ? project 
          : (project.projectName || project.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {projectName}
          </div>
        )
      }
    },
    {
      key: 'assignedTo',
      label: 'Assigned To',
      sortable: true,
      render: (value, row) => {
        const assignee = row.assignedTo
        if (!assignee) return <span className="text-gray-500">N/A</span>
        const assigneeName = typeof assignee === 'string' 
          ? assignee 
          : (assignee.employeeName || assignee.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {assigneeName}
          </div>
        )
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'Not Started': '#6B7280',
          'In Progress': '#3B82F6',
          'On Hold': '#F59E0B',
          'Completed': '#10B981',
          'Cancelled': '#EF4444',
          'Delayed': '#EF4444'
        }
        const statusColor = statusColors[value] || '#6B7280'
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              border: `1px solid ${statusColor}40`
            }}
          >
            {value || 'N/A'}
          </span>
        )
      }
    },
    {
      key: 'priority',
      label: 'Priority',
      sortable: true,
      render: (value, row) => {
        const priority = row.priority
        if (!priority) return <span className="text-gray-500">N/A</span>
        const priorityName = typeof priority === 'string' 
          ? priority 
          : (priority.priorityName || priority.name || 'N/A')
        const priorityColor = typeof priority === 'object' 
          ? (priority.priorityColor || '#6B7280')
          : '#6B7280'
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${priorityColor}20`,
              color: priorityColor,
              border: `1px solid ${priorityColor}40`
            }}
          >
            {priorityName}
          </span>
        )
      }
    },
    {
      key: 'plannedDate',
      label: 'Planned Date',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {formatDate(value)}
        </div>
      )
    },
    {
      key: 'progressPercentage',
      label: 'Progress',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <div className="w-16 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full" 
              style={{ width: `${value || 0}%` }}
            ></div>
          </div>
          <span className="text-sm text-gray-600">{value || 0}%</span>
        </div>
      )
    }
  ]

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Milestone List', path: '/projects/milestones/list', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Milestone List</h1>
          <p>Create, view, and manage all milestones</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New Milestone
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Target size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalMilestones || 0}</div>
          <div className="metric-title">Total Milestones</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.completed || 0}</div>
          <div className="metric-title">Completed</div>
          <div className="metric-subtitle">This month</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{stats.inProgress || 0}</div>
          <div className="metric-title">In Progress</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertCircle size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>+3%</span>
            </div>
          </div>
          <div className="metric-value">{stats.delayed || 0}</div>
          <div className="metric-title">Delayed</div>
          <div className="metric-subtitle">Need attention</div>
        </div>
      </div>

      {/* Milestone List Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search milestones..."
          emptyMessage="No milestones found. Create your first milestone to get started!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Milestone"
        message={`Are you sure you want to delete "${deleteItem?.milestoneName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Revision History Modal */}
      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        data={selectedMilestoneData}
        title="Milestone Revision History"
      />
    </div>
  )
}

export default MilestoneList

